import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl, subBrandAPI } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';

// New Order Tab Component
export const NewOrderTab = ({ dealers, products, orders = [], fetchData, patchOrder, onOrderCreated }) => {
  const [selectedDealer, setSelectedDealer] = useState('');
  const [dealerSearch, setDealerSearch] = useState('');
  const [showDealerList, setShowDealerList] = useState(false);
  const [dealerListIdx, setDealerListIdx] = useState(0);
  const [orderRows, setOrderRows] = useState([{ product_id: '', quantity: 1, custom_name: '', custom_price: '', sub_brand_name: '', parent_product_id: '', weight_kg: 50, variant: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [deliveryDate, setDeliveryDate] = useState(tomorrow);
  const [deliveryTime, setDeliveryTime] = useState('10:00');
  const [subBrands, setSubBrands] = useState([]);
  // Refs to each row's Qty <Input>. After product selection we auto-focus +
  // select via a useEffect (waiting for React to commit the Qty input mount).
  const qtyRefs = useRef({});
  const [focusQtyIdx, setFocusQtyIdx] = useState(null);

  useEffect(() => {
    if (focusQtyIdx == null) return;
    // Two RAFs let React commit the conditionally-rendered Qty input before
    // we look for it (and let mobile browsers paint), then focus + select.
    let raf1 = 0, raf2 = 0;
    const run = () => {
      const el = qtyRefs.current[focusQtyIdx];
      if (el && typeof el.focus === 'function') {
        el.focus();
        if (typeof el.select === 'function') {
          try { el.select(); } catch (_) { /* noop */ }
        }
      }
      setFocusQtyIdx(null);
    };
    raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(run); });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [focusQtyIdx, orderRows]);

  const updateRow = (idx, field, value) => {
    setOrderRows(orderRows.map((row, i) => i === idx ? { ...row, [field]: value } : row));
    // Auto-focus + select Qty input every time the operator picks/changes a
    // (non-empty) product on a row.
    if (field === 'product_id' && value) {
      setFocusQtyIdx(idx);
    }
  };

  useEffect(() => {
    let cancelled = false;
    subBrandAPI.getAll().then(r => { if (!cancelled) setSubBrands(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Tokenized AND-match across name + town + phone. Typing "ravi palsana" finds a dealer
  // named "Ravi" in town "Palsana" (and the reverse "palsana ravi" works too). A single
  // token still matches across any of the fields.
  const filteredDealers = dealers.filter(d => {
    if (!dealerSearch) return true;
    const tokens = dealerSearch.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return true;
    const hay = `${d.name || ''} ${d.town || ''} ${d.phone_number || ''}`.toLowerCase();
    return tokens.every(t => hay.includes(t));
  }).slice(0, 20);

  // Reset highlight when search query changes (top of filtered list).
  useEffect(() => { setDealerListIdx(0); }, [dealerSearch]);

  const pickDealer = (d) => {
    setSelectedDealer(d.id);
    setDealerSearch(d.name);
    setShowDealerList(false);
    // Auto-fill products from the dealer's MOST RECENT past order ("preferences"
    // — operators almost always re-order the same product mix). Sort by
    // created_at desc, take the first order's items, and map them into
    // orderRows. MISC items keep their sub-brand/parent/weight/variant
    // metadata so the same custom row reappears. If the dealer has no past
    // orders, the default empty row stays.
    const dealerOrders = (orders || []).filter((o) => o.dealer_id === d.id);
    if (dealerOrders.length === 0) return;
    dealerOrders.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    const latest = dealerOrders[0];
    const items = latest.items || [];
    if (items.length === 0) return;
    // On save, the backend rewrites a MISC line's product_id to point at its
    // (hidden, is_virtual) resolved product. To rebuild an editable MISC row
    // we must point product_id back at the real MISC product so isMisc(row)
    // is true and the MISC metadata block (sub-brand / parent / weight /
    // variant / rate) renders and pre-fills.
    const miscProduct = products.find((p) => p.name === 'MISC');
    const prefilled = items.map((it) => {
      const isMiscItem = !!(it.parent_product_id || it.sub_brand_name);
      return {
        product_id: isMiscItem && miscProduct ? miscProduct.id : (it.product_id || ''),
        quantity: Number(it.quantity) || 1,
        custom_name: '',
        custom_price: isMiscItem ? String(it.price || '') : '',
        sub_brand_name: it.sub_brand_name || '',
        parent_product_id: it.parent_product_id || '',
        weight_kg: Number(it.weight_kg) || 50,
        variant: it.variant || '',
      };
    }).filter((r) => r.product_id);
    if (prefilled.length > 0) {
      setOrderRows(prefilled);
      toast.success(`Pre-filled ${prefilled.length} product${prefilled.length === 1 ? '' : 's'} from this dealer's last order`);
    }
  };

  const onDealerKeyDown = (e) => {
    if (!filteredDealers.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowDealerList(true);
      setDealerListIdx((i) => Math.min(i + 1, filteredDealers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDealerListIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (showDealerList && filteredDealers[dealerListIdx]) {
        e.preventDefault();
        pickDealer(filteredDealers[dealerListIdx]);
      }
    } else if (e.key === 'Escape') {
      setShowDealerList(false);
    }
  };

  const totalQty = orderRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);

  const selectedDealerObj = dealers.find(d => d.id === selectedDealer);

  const addRow = () => setOrderRows([...orderRows, { product_id: '', quantity: 1, custom_name: '', custom_price: '', sub_brand_name: '', parent_product_id: '', weight_kg: 50, variant: '' }]);

  const removeRow = (idx) => {
    if (orderRows.length <= 1) return;
    setOrderRows(orderRows.filter((_, i) => i !== idx));
  };

  // Sorted, non-virtual product list for the row dropdown.
  const productOptions = useMemo(() => (
    [...products]
      .filter(p => !p.is_virtual)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  ), [products]);


  // MISC preference lookup built from past orders for the CURRENTLY SELECTED
  // dealer only. Dealer-scoped strictly — if the dealer has no past MISC line
  // matching the chosen sub-brand, we leave the other fields blank (no
  // cross-dealer fallback, per operator request). Keyed only by
  // `sub_brand_name`; selecting a parent product is treated as a plain edit
  // and does NOT trigger auto-fill.
  const miscLookups = useMemo(() => {
    const bySubDealer = new Map();
    if (!selectedDealer) return { bySubDealer };
    const sorted = [...(orders || [])]
      .filter((o) => o.dealer_id === selectedDealer)
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    for (const o of sorted) {
      for (const it of (o.items || [])) {
        const sub = it.sub_brand_name || '';
        const parent = it.parent_product_id || '';
        if (!sub) continue;
        if (bySubDealer.has(sub)) continue;
        bySubDealer.set(sub, {
          sub_brand_name: sub,
          parent_product_id: parent,
          weight_kg: Number(it.weight_kg) || 50,
          variant: it.variant || '',
          price: Number(it.price) || 0,
        });
      }
    }
    return { bySubDealer };
  }, [orders, selectedDealer]);

  // When sub-brand is picked on a MISC row, ALWAYS overwrite the remaining
  // MISC fields (parent / weight / variant / rate) from the historical combo
  // so every sub-brand change resets the row to that combo's defaults. The
  // operator can hand-edit ANY of those fields afterwards — only changing
  // the sub-brand itself re-triggers the auto-fill. Parent / weight /
  // variant / rate edits do NOT trigger auto-fill (no vice-versa).
  const applyMiscPrefill = (idx, trigger, value) => {
    const row = orderRows[idx];
    if (!row) return;
    if (trigger !== 'sub_brand_name') {
      // Plain edit — no auto-fill side-effects.
      setOrderRows(orderRows.map((r, i) => (i === idx ? { ...r, [trigger]: value } : r)));
      return;
    }
    const combo = value ? miscLookups.bySubDealer.get(value) : null;
    const next = { ...row, sub_brand_name: value };
    let filled = 0;
    if (combo) {
      if (combo.parent_product_id && next.parent_product_id !== combo.parent_product_id) {
        next.parent_product_id = combo.parent_product_id; filled++;
      }
      if (combo.weight_kg && Number(next.weight_kg) !== Number(combo.weight_kg)) {
        next.weight_kg = combo.weight_kg; filled++;
      }
      if ((combo.variant || '') !== (next.variant || '')) {
        next.variant = combo.variant || ''; filled++;
      }
      if (combo.price > 0 && Number(next.custom_price || 0) !== Number(combo.price)) {
        next.custom_price = String(combo.price); filled++;
      }
    }
    setOrderRows(orderRows.map((r, i) => (i === idx ? next : r)));
    if (filled > 0) {
      toast.success(`Auto-filled ${filled} MISC field${filled === 1 ? '' : 's'} from this dealer's past preferences`);
    }
  };

  const getProduct = (id) => products.find(p => p.id === id);

  const isMisc = (row) => {
    const p = getProduct(row.product_id);
    return p && p.name === 'MISC';
  };

  const getRowPrice = (row) => {
    if (isMisc(row)) return parseFloat(row.custom_price) || 0;
    const p = getProduct(row.product_id);
    return p ? p.price : 0;
  };

  const getRowTotal = (row) => getRowPrice(row) * (Number(row.quantity) || 0);

  // Discount precedence mirrors the backend / EditOrderDialog:
  //   item.discount_percent → product master → 10% fallback.
  // NewOrderTab doesn't expose a per-item discount input, so we use the
  // product master pct (falling back to 10%) so the Grand Total reflects the
  // real net amount the dealer will owe after discount.
  const getRowDiscountPct = (row) => {
    if (isMisc(row)) return 0; // MISC uses custom_price as-is, no discount.
    const p = getProduct(row.product_id);
    if (p && p.discount_percent != null) return Number(p.discount_percent);
    return 10;
  };
  const getRowNetTotal = (row) => {
    const gross = getRowTotal(row);
    const pct = getRowDiscountPct(row);
    return gross * (1 - pct / 100);
  };

  const grandTotal = orderRows.reduce((s, r) => s + getRowTotal(r), 0);
  const grandNetTotal = orderRows.reduce((s, r) => s + getRowNetTotal(r), 0);
  const grandDiscount = grandTotal - grandNetTotal;

  const handleSubmit = async () => {
    if (!selectedDealer) { toast.error('Select a dealer'); return; }
    if (!deliveryDate) { toast.error('Schedule Date is required for every order'); return; }
    // Treat empty/blank quantity as 1 (the implicit default the placeholder
    // shows). This handles the edge case where the operator picks a product
    // and submits without typing — the row is still valid.
    const normalized = orderRows.map(r => ({
      ...r,
      quantity: (r.quantity === '' || r.quantity == null) ? 1 : Number(r.quantity),
    }));
    const validRows = normalized.filter(r => r.product_id && r.quantity > 0);
    if (validRows.length === 0) { toast.error('Add at least one product'); return; }

    const items = validRows.map(r => {
      const p = getProduct(r.product_id);
      const isMiscItem = p.name === 'MISC';
      let name = p.name;
      if (isMiscItem) {
        const parent = getProduct(r.parent_product_id);
        const sub = r.sub_brand_name || 'MISC';
        const parentName = parent ? parent.name : '';
        const w = Number(r.weight_kg) || 50;
        // Default 50kg → omit weight; otherwise append "{weight}kg"
        const weightSuffix = w !== 50 ? ` ${w}kg` : '';
        const variantSuffix = r.variant ? ` ${r.variant}` : '';
        name = `${sub}${parentName ? ' ' + parentName : ''}${weightSuffix}${variantSuffix}`.trim();
      }
      const price = isMiscItem ? (parseFloat(r.custom_price) || 0) : p.price;
      const item = { product_id: p.id, product_name: name, quantity: r.quantity, price: price, total: price * r.quantity };
      if (isMiscItem) {
        item.sub_brand_name = r.sub_brand_name || null;
        item.parent_product_id = r.parent_product_id || null;
        const parent = getProduct(r.parent_product_id);
        item.parent_product_name = parent ? parent.name : null;
        item.weight_kg = Number(r.weight_kg) || 50;
        item.variant = r.variant || null;
      }
      return item;
    });

    setSubmitting(true);
    try {
      const res = await orderAPI.adminCreate({ dealer_id: selectedDealer, items, delivery_date: deliveryDate, delivery_time: deliveryTime });
      toast.success('Order created successfully');
      setSelectedDealer('');
      setDealerSearch('');
      setOrderRows([{ product_id: '', quantity: 1, custom_name: '', custom_price: '', sub_brand_name: '', parent_product_id: '', weight_kg: 50, variant: '' }]);
      // Optimistic prepend so the new order is visible the moment the
      // operator lands on the Orders tab — no full /orders refetch lag.
      // NOTE: patchOrder marks the new id as pending in AdminDashboard so
      // the follow-up fetch from the activeTab-change useEffect won't
      // clobber it if the server read hasn't caught up yet (18/Feb/2026
      // fix for the "newly saved order disappears for a few seconds" bug).
      // We intentionally do NOT call fetchData() here anymore — the parent
      // AdminDashboard useEffect already fires fetchData() on activeTab
      // change to 'orders', so a second concurrent GET /orders would just
      // race with the first one and offer no benefit.
      if (res?.data && typeof patchOrder === 'function') patchOrder(res.data);
      onOrderCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="new-order-section" className="pb-24 md:pb-20">
      <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6" style={{ color: '#2B5339' }}>New Order</h2>

      {/* Dealer Selection */}
      <Card className="p-5 bg-white border mb-6" style={{ borderColor: '#E2DFD8' }}>
        <label className="text-sm font-medium block mb-2" style={{ color: '#1E2420' }}>Select Dealer (from registered list)</label>
        <div className="relative">
          <Input
            placeholder="Search dealer by name, town or mobile (↓ ↑ to move, Enter to select)..."
            value={dealerSearch}
            onChange={(e) => { setDealerSearch(e.target.value); setShowDealerList(true); }}
            onFocus={() => setShowDealerList(true)}
            onKeyDown={onDealerKeyDown}
            className="w-full"
            data-testid="new-order-dealer-search"
          />
          {showDealerList && dealerSearch && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto" style={{ borderColor: '#E2DFD8' }}>
              {filteredDealers.length === 0 ? (
                <p className="p-3 text-sm" style={{ color: '#5C6B61' }}>No dealer found</p>
              ) : filteredDealers.map((d, i) => (
                <div
                  key={d.id}
                  className="p-3 cursor-pointer flex justify-between items-center"
                  style={{
                    borderBottom: '1px solid #E2DFD8',
                    background: i === dealerListIdx ? '#EFF4EE' : 'white',
                  }}
                  onMouseEnter={() => setDealerListIdx(i)}
                  onClick={() => pickDealer(d)}
                  data-testid="dealer-option"
                >
                  <div>
                    <p className="text-sm font-medium">{d.name}</p>
                    <p className="text-xs" style={{ color: '#5C6B61' }}>{d.town || '-'} | {d.phone_number}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selectedDealerObj && (
          <div className="mt-3 p-3 rounded flex items-center justify-between" style={{ background: '#FBF9F6' }}>
            <div>
              <p className="text-sm font-medium" style={{ color: '#2B5339' }}>{selectedDealerObj.name}</p>
              <p className="text-xs" style={{ color: '#5C6B61' }}>{selectedDealerObj.town || '-'} | {selectedDealerObj.phone_number}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => { setSelectedDealer(''); setDealerSearch(''); }} data-testid="clear-dealer-btn">Change</Button>
          </div>
        )}
      </Card>

      {/* Delivery Date */}
      <Card className="p-5 bg-white border mb-6" style={{ borderColor: '#E2DFD8' }}>
        <div className="flex items-end gap-4">
          <div>
            <label className="text-sm font-medium block mb-1" style={{ color: '#1E2420' }}>Delivery Date <span style={{ color: '#C05C35' }}>*</span></label>
            <Input type="date" required value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="w-48" data-testid="new-order-delivery-date" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1" style={{ color: '#1E2420' }}>Delivery Time</label>
            <Input type="time" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)} className="w-36" data-testid="new-order-delivery-time" />
          </div>
        </div>
      </Card>

      {/* Order Items Table */}
      <Card className="bg-white border overflow-hidden mb-6" style={{ borderColor: '#E2DFD8' }}>
        <div className="flex items-center px-3 py-2" style={{ background: '#FBF9F6', borderBottom: '1px solid #E2DFD8' }}>
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#5C6B61' }}>Order Items</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: '#FBF9F6' }}>
                <th className="text-left pl-2 pr-0 py-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>S.No</th>
                <th className="text-left pl-0 pr-3 py-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Product / Qty</th>
                <th className="text-right p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Total</th>
                <th className="text-center p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {orderRows.map((row, idx) => {
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #E2DFD8' }} data-testid="new-order-row">
                    <td className="pl-2 pr-0 py-3 text-sm align-top">{idx + 1}</td>
                    <td className="pl-0 pr-3 py-3 align-top" style={{ minWidth: '260px' }}>
                      <select
                        value={row.product_id}
                        onChange={(e) => updateRow(idx, 'product_id', e.target.value)}
                        className="w-full max-w-sm px-2 py-1.5 border rounded text-sm text-left"
                        style={{ borderColor: '#E2DFD8' }}
                        data-testid="product-select"
                      >
                        <option value="">-- Select Product --</option>
                        {productOptions.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      {isMisc(row) && (
                        <div className="mt-2 space-y-2 p-2 rounded" style={{ background: '#FFF8F2', border: '1px dashed #E89D4F' }} data-testid="misc-fields-block">
                          <select
                            value={row.sub_brand_name || ''}
                            onChange={(e) => applyMiscPrefill(idx, 'sub_brand_name', e.target.value)}
                            className="w-full px-2 py-1.5 border rounded text-sm bg-white"
                            style={{ borderColor: '#E2DFD8' }}
                            data-testid="misc-subbrand-select"
                          >
                            <option value="">-- Select Sub-Brand --</option>
                            {subBrands.map(sb => (
                              <option key={sb.id} value={sb.name}>{sb.name}</option>
                            ))}
                          </select>
                          <select
                            value={row.parent_product_id || ''}
                            onChange={(e) => applyMiscPrefill(idx, 'parent_product_id', e.target.value)}
                            className="w-full px-2 py-1.5 border rounded text-sm bg-white"
                            style={{ borderColor: '#E2DFD8' }}
                            data-testid="misc-parent-select"
                          >
                            <option value="">-- Stacks under (Main Product) --</option>
                            {[...products]
                              .filter(p => p.name !== 'MISC' && !p.is_virtual)
                              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                              .map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                          </select>
                          <div className="flex gap-2 items-center flex-wrap">
                            <label className="text-xs whitespace-nowrap" style={{ color: '#5C6B61' }}>Bag weight:</label>
                            <select
                              value={[40, 45, 50, 60].includes(Number(row.weight_kg)) && !row._weight_edit ? String(row.weight_kg) : 'edit'}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === 'edit') {
                                  // Switch to custom-input mode — keep current value as starting point.
                                  updateRow(idx, '_weight_edit', true);
                                } else {
                                  updateRow(idx, '_weight_edit', false);
                                  updateRow(idx, 'weight_kg', Number(v));
                                }
                              }}
                              className="px-2 py-1.5 border rounded text-sm bg-white"
                              style={{ borderColor: '#E2DFD8', minWidth: '80px' }}
                              data-testid="misc-weight-select"
                            >
                              <option value="40">40 kg</option>
                              <option value="45">45 kg</option>
                              <option value="50">50 kg</option>
                              <option value="60">60 kg</option>
                              <option value="edit">Edit…</option>
                            </select>
                            {(row._weight_edit || ![40, 45, 50, 60].includes(Number(row.weight_kg))) && (
                              <Input
                                type="number"
                                min="1"
                                placeholder="kg"
                                value={row.weight_kg || ''}
                                onChange={(e) => updateRow(idx, 'weight_kg', e.target.value)}
                                className="w-20 text-sm"
                                data-testid="misc-weight-custom-input"
                              />
                            )}
                            <select
                              value={['', 'GUR', 'SPL'].includes(row.variant || '') && !row._variant_edit ? (row.variant || '') : 'edit'}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === 'edit') {
                                  // Switch to custom-input mode. Don't clobber the existing variant —
                                  // the input pre-fills with whatever's there so the user can refine it.
                                  updateRow(idx, '_variant_edit', true);
                                } else {
                                  updateRow(idx, '_variant_edit', false);
                                  updateRow(idx, 'variant', v);
                                }
                              }}
                              className="px-2 py-1.5 border rounded text-sm bg-white"
                              style={{ borderColor: '#E2DFD8', minWidth: '80px' }}
                              data-testid="misc-variant-select"
                              title="Optional variant tag — e.g. GUR / SPL / custom"
                            >
                              <option value="">--</option>
                              <option value="GUR">GUR</option>
                              <option value="SPL">SPL</option>
                              <option value="edit">Edit…</option>
                            </select>
                            {(row._variant_edit || !['', 'GUR', 'SPL'].includes(row.variant || '')) && (
                              <Input
                                type="text"
                                placeholder="Variant"
                                value={row.variant || ''}
                                onChange={(e) => updateRow(idx, 'variant', e.target.value.toUpperCase())}
                                className="w-24 text-sm"
                                data-testid="misc-variant-custom-input"
                                autoFocus={row._variant_edit && !row.variant}
                              />
                            )}
                          </div>
                          <p className="text-[10px]" style={{ color: '#5C6B61' }}>
                            Tip: manage sub-brands in <b>Products</b> tab.
                          </p>
                        </div>
                      )}
                      {row.product_id && (
                        <div className="mt-2 flex items-center gap-2" data-testid="row-qty-wrap">
                          <label className="text-xs font-medium" style={{ color: '#5C6B61' }}>Qty</label>
                          <Input
                            ref={(el) => { qtyRefs.current[idx] = el; }}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            enterKeyHint="next"
                            placeholder="1"
                            value={row.quantity}
                            onFocus={(e) => { try { e.target.select(); } catch (_) { /* noop */ } }}
                            onChange={(e) => {
                              // Allow empty intermediate state. Strip everything
                              // that isn't a digit so paste of "qty: 5" still works.
                              const raw = e.target.value.replace(/[^0-9]/g, '');
                              if (raw === '') {
                                updateRow(idx, 'quantity', '');
                              } else {
                                const n = parseInt(raw, 10);
                                if (!isNaN(n) && n >= 1) updateRow(idx, 'quantity', n);
                              }
                            }}
                            onBlur={(e) => {
                              const n = parseInt(e.target.value, 10);
                              if (isNaN(n) || n < 1) updateRow(idx, 'quantity', 1);
                              else updateRow(idx, 'quantity', n);
                            }}
                            className="w-24 text-right"
                            data-testid="qty-input"
                          />
                          {/* Per-unit price displayed inline next to Qty
                              (the row Total lives in its own column on the
                              right). */}
                          <span className="text-xs" style={{ color: '#5C6B61' }} data-testid="row-unit-price">
                            {getRowPrice(row) ? `@ \u20B9${getRowPrice(row)}` : ''}
                          </span>
                        </div>
                      )}
                      {isMisc(row) && (
                        <div className="mt-2 flex items-center gap-2" data-testid="misc-price-wrap">
                          <label className="text-xs font-medium" style={{ color: '#5C6B61' }}>Rate {'\u20B9'}</label>
                          <Input
                            type="number"
                            placeholder="Price per unit"
                            value={row.custom_price}
                            onChange={(e) => updateRow(idx, 'custom_price', e.target.value)}
                            className="w-32 text-right text-sm"
                            data-testid="misc-price-input"
                          />
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-sm text-right font-bold align-top" style={{ color: '#2B5339' }}>{'\u20B9'}{getRowTotal(row).toFixed(2)}</td>
                    <td className="p-3 text-center align-top">
                      {orderRows.length > 1 && (
                        <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-7" onClick={() => removeRow(idx)} data-testid="remove-row-btn">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {/* Bottom totals row — Total Qty and Grand Total stacked in
                  the same column (Product/Qty) so the labels line up with
                  the row totals above them. */}
              <tr style={{ background: '#FBF9F6' }}>
                <td></td>
                <td className="pl-0 pr-3 py-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-bold whitespace-nowrap" data-testid="new-order-total-qty">
                      Total Qty: <span style={{ color: '#2B5339' }}>{totalQty.toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-sm font-bold mr-2">Grand Total:</span>
                      <span className="text-lg font-bold" style={{ color: '#2B5339' }} data-testid="grand-total">{'\u20B9'}{grandNetTotal.toFixed(2)}</span>
                    </div>
                    {grandDiscount > 0.005 && (
                      <div className="text-[10px]" style={{ color: '#5C6B61' }}>
                        Gross {'\u20B9'}{grandTotal.toFixed(2)} − Discount {'\u20B9'}{grandDiscount.toFixed(2)}
                      </div>
                    )}
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={addRow} data-testid="add-row-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Product Row
        </Button>
        <Button style={{ background: '#2B5339' }} onClick={handleSubmit} disabled={submitting || !selectedDealer} data-testid="submit-order-btn">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : 'Create Order'}
        </Button>
      </div>
    </div>
  );
};

